import { useEffect, useMemo, useRef, useState } from 'react';
import { Upload, Loader2, Eye } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type EarnMode = 'consolidated' | 'debit-only' | 'credit-only' | 'approved';

type ConsolidatedOrderRow = {
  id: number | null;
  order_at: string | null;
  order_name: string | null;
  debit: number;
  credit: number;
  authorized: boolean;
  previous_quota: number;
  new_earn_quota: number;
};

type ConsolidatedRow = {
  customer_db_id: number;
  customer_id: number | string | null;
  earn_code: string | null;
  rebate_amount: number;
  top_up_amount: number;
  new_earn_quota: number;
  orders: ConsolidatedOrderRow[];
};

type DetailRow = {
  id: number;
  customer_db_id: number | null;
  customer_id: number | string | null;
  earn_code: string | null;
  order_at: string | null;
  order_name: string | null;
  debit: number;
  credit: number;
  authorized: boolean;
  previous_quota: number;
  new_earn_quota: number;
  entry_kind: 'debit' | 'credit';
};

type EarnDataResponse = {
  mode: EarnMode;
  count: number;
  results: Array<ConsolidatedRow | DetailRow>;
};

function toNum(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function fmtDate(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function parseEarnDataResponse(raw: unknown): EarnDataResponse {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const modeRaw = String(obj.mode ?? 'consolidated') as EarnMode;
  const mode: EarnMode =
    modeRaw === 'debit-only' || modeRaw === 'credit-only' || modeRaw === 'approved' ? modeRaw : 'consolidated';
  const rows = Array.isArray(obj.results) ? obj.results : [];

  if (mode === 'consolidated') {
    const results: ConsolidatedRow[] = rows.map((item) => {
      const row = item as Record<string, unknown>;
      const ordersRaw = Array.isArray(row.orders) ? row.orders : [];
      const orders: ConsolidatedOrderRow[] = ordersRaw.map((o) => {
        const order = o as Record<string, unknown>;
        return {
          id: Number.isFinite(Number(order.id)) ? Number(order.id) : null,
          order_at: order.order_at == null ? null : String(order.order_at),
          order_name: order.order_name == null ? null : String(order.order_name),
          debit: toNum(order.debit),
          credit: toNum(order.credit),
          authorized: Boolean(order.authorized),
          previous_quota: toNum(order.previous_quota),
          new_earn_quota: toNum(order.new_earn_quota),
        };
      });
      return {
        customer_db_id: Number(row.customer_db_id ?? 0),
        customer_id: row.customer_id == null ? null : (row.customer_id as number | string),
        earn_code: row.earn_code == null ? null : String(row.earn_code),
        rebate_amount: toNum(row.rebate_amount),
        top_up_amount: toNum(row.top_up_amount),
        new_earn_quota: toNum(row.new_earn_quota),
        orders,
      };
    });
    return { mode, count: toNum(obj.count), results };
  }

  const detailResults: DetailRow[] = rows
    .map((item) => {
      const row = item as Record<string, unknown>;
      const id = Number(row.id);
      if (!Number.isFinite(id)) return null;
      return {
        id,
        customer_db_id: Number.isFinite(Number(row.customer_db_id)) ? Number(row.customer_db_id) : null,
        customer_id: row.customer_id == null ? null : (row.customer_id as number | string),
        earn_code: row.earn_code == null ? null : String(row.earn_code),
        order_at: row.order_at == null ? null : String(row.order_at),
        order_name: row.order_name == null ? null : String(row.order_name),
        debit: toNum(row.debit),
        credit: toNum(row.credit),
        authorized: Boolean(row.authorized),
        previous_quota: toNum(row.previous_quota),
        new_earn_quota: toNum(row.new_earn_quota),
        entry_kind: String(row.entry_kind ?? 'credit') === 'debit' ? 'debit' : 'credit',
      } as DetailRow;
    })
    .filter(Boolean) as DetailRow[];

  return { mode, count: toNum(obj.count), results: detailResults };
}

export function EarnTransactionPage() {
  const [mode, setMode] = useState<EarnMode>('consolidated');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EarnDataResponse>({ mode: 'consolidated', count: 0, results: [] });

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [authorizing, setAuthorizing] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<ConsolidatedRow | null>(null);

  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadSummary, setUploadSummary] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Render branch must follow the payload mode, not the selected tab mode,
  // otherwise a fast tab switch can render stale row shape and crash.
  const isConsolidated = data.mode === 'consolidated';
  const consolidatedRows = isConsolidated ? ((data.results as ConsolidatedRow[]) ?? []) : [];
  const detailRows = isConsolidated ? [] : ((data.results as DetailRow[]) ?? []);

  const sampleCsvHref = useMemo(() => {
    const sample = ['customer_id,order_at,debit', '1001,2026-02-20T10:00:00+08:00,12.50'].join('\n');
    return `data:text/csv;charset=utf-8,${encodeURIComponent(sample)}`;
  }, []);

  async function loadData(targetMode: EarnMode = mode) {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('mode', targetMode);
      const resp = await fetch(`/v1/api/earn-code-manager/earn-record/data/?${qs.toString()}`, { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const raw = (await resp.json()) as unknown;
      const parsed = parseEarnDataResponse(raw);
      setData(parsed);
      setSelectedIds(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load earn record data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  async function authorizeByIds(ids: number[]) {
    if (!ids.length || authorizing) return;
    setAuthorizing(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/earn-code-manager/earn-record/authorize-transactions/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ earn_record_ids: ids }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Authorize failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      await loadData(mode);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to authorize transactions.');
    } finally {
      setAuthorizing(false);
    }
  }

  async function uploadCsv() {
    if (!uploadFile || uploading) return;
    setUploading(true);
    setError(null);
    setUploadSummary(null);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const resp = await fetch('/v1/api/earn-code-manager/earn-record/debit-bulk-upload/', {
        method: 'POST',
        body: fd,
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Upload failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const result = (await resp.json()) as Record<string, unknown>;
      const inserted = toNum(result.inserted_count);
      const failed = toNum(result.failed_count);
      setUploadSummary(`Upload completed. Inserted ${inserted} row(s), failed ${failed} row(s).`);
      setUploadFile(null);
      await loadData(mode);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload CSV.');
    } finally {
      setUploading(false);
    }
  }

  function onDropFile(file: File | null) {
    if (!file) return;
    setUploadFile(file);
  }

  return (
    <div className="space-y-6">
      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader>
          <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Special Event Debit Upload</CardTitle>
          <CardDescription className="text-gray-600 dark:text-neutral-400">
            Upload CSV to insert special debit rows and auto-recompute cascading quota.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div
            className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-gray-300 dark:border-neutral-700'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const file = e.dataTransfer.files?.[0] ?? null;
              onDropFile(file);
            }}
          >
            <Upload className="mx-auto h-6 w-6 text-gray-500 dark:text-neutral-400" />
            <p className="mt-2 text-sm text-gray-700 dark:text-neutral-300">
              Drag and drop your CSV here, or{' '}
              <button
                type="button"
                className="underline"
                onClick={() => fileInputRef.current?.click()}
              >
                browse file
              </button>
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-500">Required columns: customer_id, order_at, debit</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => onDropFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm text-gray-700 dark:text-neutral-300">
              {uploadFile ? `Selected: ${uploadFile.name}` : 'No file selected'}
            </div>
            <div className="flex items-center gap-2">
              <a
                href={sampleCsvHref}
                download="earn_record_special_debit_template.csv"
                className="text-sm underline text-gray-700 dark:text-neutral-300"
              >
                Download sample CSV template
              </a>
              <Button onClick={() => void uploadCsv()} disabled={!uploadFile || uploading}>
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Upload
              </Button>
            </div>
          </div>
          {uploadSummary ? <p className="text-sm text-green-700 dark:text-green-400">{uploadSummary}</p> : null}
        </CardContent>
      </Card>

      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader className="space-y-4">
          <div>
            <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Earn Record Transactions</CardTitle>
          </div>
          <Tabs value={mode} onValueChange={(v) => setMode(v as EarnMode)}>
            <TabsList variant="line" className="w-full justify-start">
              <TabsTrigger variant="line" value="consolidated">Consolidated</TabsTrigger>
              <TabsTrigger variant="line" value="debit-only">Debit Only</TabsTrigger>
              <TabsTrigger variant="line" value="credit-only">Credit Only</TabsTrigger>
              <TabsTrigger variant="line" value="approved">Approved</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          ) : null}

          {!isConsolidated ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-neutral-400">{data.count} row(s)</div>
              {mode !== 'approved' ? (
                <Button size="sm" onClick={() => void authorizeByIds(Array.from(selectedIds))} disabled={selectedIds.size === 0 || authorizing}>
                  {authorizing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Authorize Selected ({selectedIds.size})
                </Button>
              ) : null}
            </div>
          ) : null}

          <div className="rounded-lg border border-gray-200 dark:border-neutral-800 overflow-hidden">
            <Table>
              <TableHeader>
                {isConsolidated ? (
                  <TableRow>
                    <TableHead>Customer ID</TableHead>
                    <TableHead>Earn Code</TableHead>
                    <TableHead className="text-right">Top-up Amount</TableHead>
                    <TableHead className="text-right">Rebate Amount</TableHead>
                    <TableHead className="text-right">New Quota</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <input
                        type="checkbox"
                        checked={detailRows.length > 0 && detailRows.every((r) => selectedIds.has(r.id))}
                        onChange={(e) => {
                          if (e.currentTarget.checked) setSelectedIds(new Set(detailRows.map((r) => r.id)));
                          else setSelectedIds(new Set());
                        }}
                        disabled={mode === 'approved'}
                      />
                    </TableHead>
                    <TableHead>Customer ID</TableHead>
                    <TableHead>Earn Code</TableHead>
                    <TableHead>Order At</TableHead>
                    <TableHead>Order Name</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Prev Quota</TableHead>
                    <TableHead className="text-right">New Quota</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                )}
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isConsolidated ? 7 : 10} className="py-8 text-center text-gray-500 dark:text-neutral-400">
                      <div className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading data...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : data.results.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isConsolidated ? 7 : 10} className="py-8 text-center text-gray-500 dark:text-neutral-400">
                      No records found
                    </TableCell>
                  </TableRow>
                ) : isConsolidated ? (
                  consolidatedRows.map((row) => (
                    <TableRow key={`${row.customer_db_id}-${row.customer_id ?? 'na'}`}>
                      <TableCell>{row.customer_id ?? '-'}</TableCell>
                      <TableCell className="font-mono">{row.earn_code ?? '-'}</TableCell>
                      <TableCell className="text-right">{row.top_up_amount.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{row.rebate_amount.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{row.new_earn_quota.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{row.orders.length}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setDetailRow(row);
                            setDetailsOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  detailRows.map((row) => {
                    const checked = selectedIds.has(row.id);
                    return (
                      <TableRow key={row.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={mode === 'approved'}
                            onChange={(e) => {
                              const isChecked = e.currentTarget.checked;
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (isChecked) next.add(row.id);
                                else next.delete(row.id);
                                return next;
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>{row.customer_id ?? '-'}</TableCell>
                        <TableCell className="font-mono">{row.earn_code ?? '-'}</TableCell>
                        <TableCell>{fmtDate(row.order_at)}</TableCell>
                        <TableCell>{row.order_name ?? '-'}</TableCell>
                        <TableCell className="text-right">{row.debit.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{row.credit.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{row.previous_quota.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{row.new_earn_quota.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={row.authorized ? 'default' : 'secondary'}>
                            {row.authorized ? 'Approved' : 'Pending'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Consolidated Order Details</DialogTitle>
            <DialogDescription>
              {detailRow ? `Customer ${detailRow.customer_id ?? '-'} • Earn Code ${detailRow.earn_code ?? '-'}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order At</TableHead>
                  <TableHead>Order Name</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Prev Quota</TableHead>
                  <TableHead className="text-right">New Quota</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!detailRow || detailRow.orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-gray-500 dark:text-neutral-400">
                      No order details
                    </TableCell>
                  </TableRow>
                ) : (
                  detailRow.orders.map((o, idx) => (
                    <TableRow key={`${o.id ?? 'na'}-${idx}`}>
                      <TableCell>{fmtDate(o.order_at)}</TableCell>
                      <TableCell>{o.order_name ?? '-'}</TableCell>
                      <TableCell className="text-right">{o.debit.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{o.credit.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{o.previous_quota.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{o.new_earn_quota.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant={o.authorized ? 'default' : 'secondary'}>
                          {o.authorized ? 'Approved' : 'Pending'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
